import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { useState, useRef, useEffect } from 'react';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
  Code,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image as ImageIcon,
  Youtube as YoutubeIcon,
  Undo,
  Redo,
  RemoveFormatting,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Table as TableIcon,
  Palette,
  Highlighter,
  Plus,
  Trash2,
  ChevronDown,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  Search,
  Replace,
  Type
} from 'lucide-react';

const MenuBar = ({ editor, onFindReplace }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const colorRef = useRef(null);
  const highlightRef = useRef(null);
  const tableRef = useRef(null);
  const fontSizeRef = useRef(null);

  const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (colorRef.current && !colorRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
      if (highlightRef.current && !highlightRef.current.contains(e.target)) {
        setShowHighlightPicker(false);
      }
      if (tableRef.current && !tableRef.current.contains(e.target)) {
        setShowTableMenu(false);
      }
      if (fontSizeRef.current && !fontSizeRef.current.contains(e.target)) {
        setShowFontSizeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!editor) return null;

  const colors = [
    '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
    '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
    '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
  ];

  const highlightColors = [
    '#ffff00', '#00ff00', '#00ffff', '#ff00ff', '#ff0000', '#0000ff',
    '#fef3bd', '#d4edda', '#d1ecf1', '#f8d7da', '#e2e3e5', '#cce5ff',
  ];

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  };

  const addYoutube = () => {
    const url = window.prompt('Enter YouTube URL:');
    if (url) {
      editor.chain().focus().setYoutubeVideo({ src: url }).run();
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    setShowTableMenu(false);
  };

  return (
    <div className="rich-editor-toolbar">
      {/* Text Formatting */}
      <div className="toolbar-section">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`toolbar-icon-btn ${editor.isActive('bold') ? 'active' : ''}`}
          title="Bold (Ctrl+B)"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`toolbar-icon-btn ${editor.isActive('italic') ? 'active' : ''}`}
          title="Italic (Ctrl+I)"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`toolbar-icon-btn ${editor.isActive('underline') ? 'active' : ''}`}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`toolbar-icon-btn ${editor.isActive('strike') ? 'active' : ''}`}
          title="Strikethrough"
        >
          <Strikethrough size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          className={`toolbar-icon-btn ${editor.isActive('subscript') ? 'active' : ''}`}
          title="Subscript"
        >
          <SubscriptIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          className={`toolbar-icon-btn ${editor.isActive('superscript') ? 'active' : ''}`}
          title="Superscript"
        >
          <SuperscriptIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`toolbar-icon-btn ${editor.isActive('code') ? 'active' : ''}`}
          title="Inline Code"
        >
          <Code size={16} />
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Font Size */}
      <div className="toolbar-section">
        <div className="toolbar-dropdown" ref={fontSizeRef}>
          <button
            type="button"
            onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
            className={`toolbar-icon-btn ${showFontSizeMenu ? 'active' : ''}`}
            title="Font Size"
          >
            <Type size={16} />
          </button>
          {showFontSizeMenu && (
            <div className="font-size-dropdown">
              {fontSizes.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
                    setShowFontSizeMenu(false);
                  }}
                >
                  {size}
                </button>
              ))}
              <button
                type="button"
                className="reset-btn"
                onClick={() => {
                  editor.chain().focus().unsetMark('textStyle').run();
                  setShowFontSizeMenu(false);
                }}
              >
                Reset Size
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="toolbar-divider" />

      {/* Text Color & Highlight */}
      <div className="toolbar-section">
        <div className="toolbar-dropdown" ref={colorRef}>
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className={`toolbar-icon-btn ${showColorPicker ? 'active' : ''}`}
            title="Text Color"
          >
            <Palette size={16} />
          </button>
          {showColorPicker && (
            <div className="color-picker-dropdown">
              <div className="color-grid">
                {colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className="color-swatch"
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      editor.chain().focus().setColor(color).run();
                      setShowColorPicker(false);
                    }}
                    title={color}
                  />
                ))}
              </div>
              <button
                type="button"
                className="color-reset"
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
              >
                Reset Color
              </button>
            </div>
          )}
        </div>

        <div className="toolbar-dropdown" ref={highlightRef}>
          <button
            type="button"
            onClick={() => setShowHighlightPicker(!showHighlightPicker)}
            className={`toolbar-icon-btn ${editor.isActive('highlight') ? 'active' : ''}`}
            title="Highlight"
          >
            <Highlighter size={16} />
          </button>
          {showHighlightPicker && (
            <div className="color-picker-dropdown">
              <div className="color-grid highlight-grid">
                {highlightColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className="color-swatch"
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      editor.chain().focus().toggleHighlight({ color }).run();
                      setShowHighlightPicker(false);
                    }}
                    title={color}
                  />
                ))}
              </div>
              <button
                type="button"
                className="color-reset"
                onClick={() => {
                  editor.chain().focus().unsetHighlight().run();
                  setShowHighlightPicker(false);
                }}
              >
                Remove Highlight
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="toolbar-divider" />

      {/* Headings */}
      <div className="toolbar-section">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`toolbar-icon-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
          title="Heading 1"
        >
          <Heading1 size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`toolbar-icon-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
          title="Heading 2"
        >
          <Heading2 size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`toolbar-icon-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
          title="Heading 3"
        >
          <Heading3 size={16} />
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Text Alignment */}
      <div className="toolbar-section">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`toolbar-icon-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`toolbar-icon-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`toolbar-icon-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
          title="Align Right"
        >
          <AlignRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={`toolbar-icon-btn ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
          title="Justify"
        >
          <AlignJustify size={16} />
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Lists & Blocks */}
      <div className="toolbar-section">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`toolbar-icon-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
          title="Bullet List"
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`toolbar-icon-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
          title="Ordered List"
        >
          <ListOrdered size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`toolbar-icon-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
          title="Quote"
        >
          <Quote size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="toolbar-icon-btn"
          title="Horizontal Rule"
        >
          <Minus size={16} />
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Table */}
      <div className="toolbar-section">
        <div className="toolbar-dropdown" ref={tableRef}>
          <button
            type="button"
            onClick={() => setShowTableMenu(!showTableMenu)}
            className={`toolbar-icon-btn ${showTableMenu ? 'active' : ''}`}
            title="Table"
          >
            <TableIcon size={16} />
          </button>
          {showTableMenu && (
            <div className="table-menu-dropdown">
              <button type="button" onClick={insertTable}>
                <Plus size={14} />
                Insert Table (3×3)
              </button>
              {editor.can().addColumnAfter() && (
                <>
                  <button type="button" onClick={() => { editor.chain().focus().addColumnAfter().run(); setShowTableMenu(false); }}>
                    Add Column After
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().addColumnBefore().run(); setShowTableMenu(false); }}>
                    Add Column Before
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().addRowAfter().run(); setShowTableMenu(false); }}>
                    Add Row After
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().addRowBefore().run(); setShowTableMenu(false); }}>
                    Add Row Before
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().deleteColumn().run(); setShowTableMenu(false); }}>
                    Delete Column
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().deleteRow().run(); setShowTableMenu(false); }}>
                    Delete Row
                  </button>
                  <button type="button" className="danger" onClick={() => { editor.chain().focus().deleteTable().run(); setShowTableMenu(false); }}>
                    <Trash2 size={14} />
                    Delete Table
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="toolbar-divider" />

      {/* Links & Media */}
      <div className="toolbar-section">
        <button
          type="button"
          onClick={addLink}
          className={`toolbar-icon-btn ${editor.isActive('link') ? 'active' : ''}`}
          title="Add Link"
        >
          <LinkIcon size={16} />
        </button>
        <button
          type="button"
          onClick={addImage}
          className="toolbar-icon-btn"
          title="Add Image"
        >
          <ImageIcon size={16} />
        </button>
        <button
          type="button"
          onClick={addYoutube}
          className="toolbar-icon-btn"
          title="Embed YouTube"
        >
          <YoutubeIcon size={16} />
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Undo/Redo & Clear */}
      <div className="toolbar-section">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="toolbar-icon-btn"
          title="Undo (Ctrl+Z)"
        >
          <Undo size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="toolbar-icon-btn"
          title="Redo (Ctrl+Y)"
        >
          <Redo size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          className="toolbar-icon-btn"
          title="Clear Formatting"
        >
          <RemoveFormatting size={16} />
        </button>
        <button
          type="button"
          onClick={onFindReplace}
          className="toolbar-icon-btn"
          title="Find & Replace (Ctrl+H)"
        >
          <Search size={16} />
        </button>
      </div>
    </div>
  );
};

// Custom TextStyle extension with fontSize support
const CustomTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize || null,
        renderHTML: attributes => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },
});

const RichTextEditor = ({ content, onChange, theme, showFindReplace, onToggleFindReplace }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable extensions that we're configuring separately
        link: false,
        underline: false,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rich-editor-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'rich-editor-link',
        },
      }),
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'rich-editor-youtube',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start writing your content...',
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'rich-editor-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      CustomTextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Subscript,
      Superscript,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when prop changes externally
  if (editor && content !== editor.getHTML() && content === '') {
    editor.commands.setContent('');
  }

  return (
    <div className="rich-editor-container" data-theme={theme}>
      <MenuBar editor={editor} onFindReplace={onToggleFindReplace} />
      <EditorContent editor={editor} className="rich-editor-content" />
    </div>
  );
};

export default RichTextEditor;
